import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { MotionDiv } from '@/components/element/MotionDiv';
import { SimpleTextFormatWithLink } from '@/components/element/SimpleTextFormat';
import { UrlCopyButton } from '@/components/element/UrlCopyButton';
import { usePaymentTasksInvalidate } from '@/hooks/api/0960_payment_tasks';
import { useDestroyPaymentTask } from '@/hooks/api/0990_destroy_payment_task';
import { usePaymentTaskInvalidate } from '@/hooks/api/1000_payment_task';
import { useUnarchivePaymentTask } from '@/hooks/api/1001_unarchive_payment_tasks';
import { useCreatePaymentTaskSubscription } from '@/hooks/api/1030_create_payment_task_subscription';
import { useDestroyPaymentTaskSubscriptions } from '@/hooks/api/1040_destroy_payment_task_subscription';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { dateFormat, isBackofficeURL } from '@/utils';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalMessageConfirm } from '@/components/element/modal/ModalMessageConfirm';

export const TaskSummary = () => {
  const location = useLocation();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const [editPagePath, setEditPagePath] = useState<string>('');
  const [subscribed, setSubscribed] = useState<boolean>(() => {
    return task!.payment_task_subscriptions.some((d) => d.user.id == me!.id);
  });

  const { setShowLoading } = useLoading();
  const navigate = useNavigate();
  const showDeleteMessageButtonRef = useRef<HTMLButtonElement>(null);
  const {
    mutate: mutateDestroyPaymentTask,
    isPending: isPendingDestroyPaymentTask,
    isSuccess: isSuccessDestroyPaymentTask,
    error: errorDestroyPaymentTask,
  } = useDestroyPaymentTask();
  const { invalidate: invalidatePaymentTasks } = usePaymentTasksInvalidate(_w_!.identifier);
  useEffect(() => setShowLoading(isPendingDestroyPaymentTask), [isPendingDestroyPaymentTask]);
  useEffect(() => toastApiError(errorDestroyPaymentTask), [errorDestroyPaymentTask]);

  useEffect(() => {
    if (isSuccessDestroyPaymentTask) {
      invalidatePaymentTasks();
      toastSuccess('支払タスクを削除しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${purchase_order_id}`);
    }
  }, [isSuccessDestroyPaymentTask]);

  const onDeleteClicked = () => {
    mutateDestroyPaymentTask({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      id: task!.id,
    });
  };

  useEffect(() => {
    if (isBackofficeURL(location.pathname)) {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/backoffice/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/payment_tasks/${
            task!.id
          }/edit`
      );
    } else {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/payment_tasks/${task!.id}/edit`
      );
    }
  }, [location.pathname]);

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク基本情報</h6>
        </div>
        <div className="d-flex align-items-center">
          <UrlCopyButton
            url={`${process.env.REACT_APP_SITE_URL}/w/${_w_!.identifier}/teams/${
              _t_!.id
            }/purchase_orders/${purchase_order_id}/payment_tasks/${task!.id}`}
          />
          <ModalMessageConfirm
            modalId="deleteMessageModal"
            message={
              '支払予定との違いにより必要がないタスクは、\n記録のためにも削除ではなく0円として処理をするか、\n削除した上で支払予定を変更してください'
            }
            showModalButtonRef={showDeleteMessageButtonRef}
            okClicked={() => onDeleteClicked()}
            okButtonLabel={'削除する'}
            cancelButtonLabel={'キャンセル'}
          />

          <Link to={editPagePath} className="btn btn-outline-success btn-icon-only">
            <i className="fas fa-edit"></i>
          </Link>
          <div className="dropstart ms-3">
            <a href="" className="text-secondary" id="dropdownMarketingCard" data-bs-toggle="dropdown">
              <i className="fas fa-ellipsis-v"></i>
            </a>
            <ul className="dropdown-menu dropdown-menu-lg-start px-2 py-3" aria-labelledby="dropdownMarketingCard">
              <li>{task!.archived && <UnarchiveLink />}</li>
              <li>
                <button
                  className="dropdown-item border-radius-md text-danger"
                  onClick={() => showDeleteMessageButtonRef.current!.click()}
                >
                  <i className="fas fa-trash-alt me-2"></i>タスクの削除
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">タスク名</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{task!.name}</div>
        </div>
        <hr className="horizontal dark" />
        {task!.archived && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">アーカイブ</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                <span className="badge badge-danger">アーカイブ済</span>
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">タスク担当者</div>
          <div className="d-flex align-items-center">
            <img src={task!.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
            <div className="font-weight-bold px-0 text-sm text-dark">{task!.user.user_account.name}</div>
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">マイリスト登録ユーザー</div>
          <div className="d-flex align-items-center justify-content-center">
            <div className="avatar-group text-nowrap overflow-hidden flex-1">
              {task!.payment_task_subscriptions.map((d) => (
                <img
                  key={`pts-${d.id}`}
                  src={d.user.user_account.icon_file_url}
                  className="avatar avatar-xs rounded-circle"
                />
              ))}
            </div>
            {subscribed ? (
              <UnSubscribeLink setSubscribed={setSubscribed} />
            ) : (
              <SubscribeLink setSubscribed={setSubscribed} />
            )}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">請求月</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">
            {task!.payment_schedule_detail.payment_schedule_outline.year_month}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">期限日</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">
            {task!.due_date ? dateFormat({ date: task!.due_date }) : '−'}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">メモ</div>
          <div className="px-0 text-xs break-word">
            <SimpleTextFormatWithLink content={task!.description || ''} />
          </div>
        </div>
      </div>
    </MotionDiv>
  );
};

type SubscriptionLinkProps = {
  setSubscribed: Dispatch<SetStateAction<boolean>>;
};

// 購読ボタン
const SubscribeLink = ({ setSubscribed }: SubscriptionLinkProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { mutate, data, isPending, isSuccess, error } = useCreatePaymentTaskSubscription();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      setSubscribed((_) => true);
      task!.payment_task_subscriptions.push(data);
      toastSuccess('追加しました');
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
    });
  };

  return (
    <>
      <ModalConfirm
        modalId={'subscribeConfirm'}
        okClicked={onClick}
        message={
          'この支払タスクをマイリストに追加しますか？\nマイリストに登録すると一覧画面で簡単に見つけられるようになります'
        }
      />

      <button
        data-bs-toggle="modal"
        data-bs-target={`#subscribeConfirm`}
        className="btn bg-gradient-dark btn-icon-only ms-2"
      >
        <i className="fas fa-rss"></i>
      </button>
    </>
  );
};

// 購読解除ボタン
const UnSubscribeLink = ({ setSubscribed }: SubscriptionLinkProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { mutate, data, isPending, isSuccess, error } = useDestroyPaymentTaskSubscriptions();
  const subscription = task!.payment_task_subscriptions.find((d) => d.user.id == me!.id);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      setSubscribed(false);
      task!.payment_task_subscriptions = task!.payment_task_subscriptions.filter((d) => d.id != subscription!.id);
      toastSuccess('解除しました');
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      id: subscription!.id,
    });
  };

  return (
    <>
      <button
        data-bs-toggle="modal"
        data-bs-target={`#unSubscribeConfirm`}
        className="btn bg-gradient-danger btn-icon-only ms-2"
      >
        <i className="fas fa-rss"></i>
      </button>

      <ModalConfirm
        modalId={'unSubscribeConfirm'}
        okClicked={onClick}
        message={'この支払タスクをマイリストから解除しますか？'}
      />
    </>
  );
};

// アーカイブ解除ボタン
const UnarchiveLink = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { mutate, isPending, isSuccess, error } = useUnarchivePaymentTask();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { invalidate } = usePaymentTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('アーカイブを解除しました');
      invalidate();
    }
  }, [isSuccess]);

  const onArchiveClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      id: task!.id,
    });
  };

  return (
    <button className="dropdown-item border-radius-md" onClick={onArchiveClick}>
      <i className="fas fa-box-open me-2"></i>アーカイブを解除
    </button>
  );
};
